import { Injectable } from '@angular/core';
import { of, ReplaySubject, Subject } from 'rxjs';
import moment from 'moment';
import { ServerConnectionService } from '../server-connection.service';
import { CoreModule } from '../../core.module';

import { ModalService } from '../modal.service';
import { SettingsService } from './settings.service';
import { GatekeeperService } from './gatekeeper.service';

@Injectable({
	providedIn: CoreModule,
})
export class RecurringFeatureService {
	public originalFeature: any;

	public scheduleParameters: {
		featureId: string;
		siteAdvertId: string;
		frequency?: number;
		pubDate: number;
		userId: string;
		customerId?: string;
		paymentMethodId?: string;
	} = {
			featureId: '',
			siteAdvertId: '',
			pubDate: 0,
			userId: '',
		};

	public isSchedulingConflict$ = new Subject<boolean>();

	public showRecurringPromotionOption$ = new ReplaySubject<boolean>(1);

	public originalFeature$ = new ReplaySubject<any>(1);

	private _upcomingPromotions = new ReplaySubject<Object>(1);

	constructor(
		private scs: ServerConnectionService,
		private modalService: ModalService,
		private settingsService: SettingsService,
		private gatekeeperService: GatekeeperService,
	) { }

	get upcomingPromotions$() {
		return this._upcomingPromotions.asObservable();
	}

	async checkIfAvailable() {
		const value = await this.settingsService.getRecurringPromotionsSetting();

		const isAuthorized = this.gatekeeperService.isAuthorized(value);

		this.showRecurringPromotionOption$.next(isAuthorized);
	}

	close() {
		this.modalService.close('recurring-promotion');
	}

	initialize(feature) {
		this.originalFeature = feature;
		this.originalFeature$.next(feature);
		const params = {
			siteAdvertId: feature._siteAdvert,
			pubDate: +moment().format('YYYYMMDD'),
			featureId: feature._id,
		};

		Object.assign(this.scheduleParameters, params);
	}

	setFrequency(frequency) {
		this.scheduleParameters.frequency = frequency;
		this.checkSchedule();
	}

	setPaymentInformation(customerId, paymentMethodId, userId) {
		this.scheduleParameters.customerId = customerId;
		this.scheduleParameters.paymentMethodId = paymentMethodId;
		this.scheduleParameters.userId = userId;
	}

	setPubDate(pubDate) {
		this.scheduleParameters.pubDate = pubDate;
	}

	checkSchedule() {
		if (this._hasInvalidScheduleParameters()) {
			// TODO: Show error message
			return;
		}

		this.scs.http$('POST', '/api/recurring-feature/check-schedule', null, this.scheduleParameters).subscribe(
			(result: { success: boolean; promotions: any[] }) => this.isSchedulingConflict$.next(!result.success),
		);
	}

	cancelRecurringFeature(id) {
		return this.scs.http$('POST', '/api/recurring-feature/cancel', null, { id });
	}

	schedule() {
		if (this._hasInvalidScheduleParameters()) {
			return of(new Error('Invalid Schedule Parameters'));
		}

		return this.scs.http$('POST', '/api/recurring-feature/create', null, this.scheduleParameters);
	}

	_hasInvalidScheduleParameters() {
		return !this.scheduleParameters?.siteAdvertId || !this.scheduleParameters?.frequency;
	}
}
